import {useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912): nulls-last and numeric-vs-string live in the
// library, not in a per-module copy.
import {useTableSort} from '@flexinet/ui-components';

import {fetchRecords} from '../services/OrdersService';
import {Order} from '../types';
import {ordersQueryKeys} from './ordersQueryKeys';

// Module-scoped global filter state, shared by the filter button, the filter drawer and the
// table (docs/patterns/global-filter-store-pattern.md). Starts on PENDING orders.
let globalFilters: Record<string, string> = { status: 'PENDING' };
const filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useOrdersFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return { filters, handleSetFilters, handleClearFilters };
};

// Page size is a server concern fixed by the module (§9), never a user choice.
const PAGE_SIZE = 10;

// What the request asks for. The listOrders resolver ignores `sort` and always reads
// record-type-created-index newest first; it is sent so the request states the order the
// table expects.
const SERVER_SORT = {column: 'created', direction: 'desc'} as const;

// NEWEST FIRST, on `created`: the order the server returns, so first paint agrees with it.
// `created` is nullable in the schema (AWSDateTime), but every listed row carries it: a row
// without it is not in record-type-created-index at all.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<Order> = {
    columnAccessor: 'created',
    direction: 'desc',
};

const matchesKeyword = (item: Order, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [item.id, item.subscriptionId, item.status, item.customerId];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

export const useOrders = (context: 'customer' | 'reports' = 'customer') => {
    const queryClient = useQueryClient();
    const {filters: userFilters} = useOrdersFilter();
    const {id: customerId} = useParams();

    // Component state, not a module-scoped store: the search input (TableSearchForm) and the
    // table that reads the keyword are the SAME component, RecordsTable.
    const [keyword, setKeyword] = useState('');

    // The customer comes from the URL (the Orders tab of customers/:id), the rest from the
    // filter drawer. Both are sent to the server and both are in the key.
    const mergedFilter: Record<string, string> = {
        ...userFilters,
        ...((context === 'customer' && customerId) ? {customerId} : {})
    };

    // Only what the server receives is in the key. The keyword is applied client-side below,
    // so keying on it would refetch identical pages per keystroke.
    const query = useInfiniteQuery({
        queryKey: ordersQueryKeys.list(mergedFilter),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({limit: PAGE_SIZE, sort: SERVER_SORT, filter: mergedFilter, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        // Every mutation in this module invalidates ['orders'], so 30s stops a tab switch from
        // refetching without hiding an edit.
        staleTime: 30_000,
    });

    const items: Order[] =
        query.data?.pages?.flatMap(page => (page && 'items' in page && page.items) ? page.items : []) ?? [];

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ Client-side filter and sort cover the pages FETCHED SO FAR. Under token pagination
    // the browser cannot order or search rows it has not loaded; that is a property of the
    // model, not a bug (§9). Sort state is component-level: useTableSort holds it.
    const {sortStatus, setSortStatus, sorted} = useTableSort<Order>(
        items.filter(item => matchesKeyword(item, needle)),
        DEFAULT_SORT_STATUS,
    );

    const lastPage = query.data?.pages?.[query.data.pages.length - 1];

    return {
        // The loaded set BEFORE keyword filtering: what the footer's "N of M loaded" is
        // measured against.
        items,
        visibleItems: sorted,
        keyword,
        filters: userFilters,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingMore: query.isFetchingNextPage,
        // The server's own cursor, never a stand-in: opaque to this client (§9).
        nextToken: query.hasNextPage ? (lastPage?.nextToken ?? null) : null,
        // Loads the NEXT page. Guarded on isFetchingNextPage too, so scroll-to-bottom and
        // Load More cannot start overlapping requests for the same cursor. It used to
        // invalidate the list, which refetched page 1 and never reached page 2.
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        // Reload starts again from page 1, for every list of every customer.
        reloadRecords: () => queryClient.invalidateQueries({queryKey: ordersQueryKeys.all}),
        handleFilterByKeyword: (newKeyword: string) => setKeyword(newKeyword),
    };
};
